// Desk-owned data that has no CRM object yet: sign-in accounts, the Educational Folders Vault and its files.
// Every call here goes to the standalone server's own API (same origin). When the desk is merged into the CRM these
// become Twenty objects (workspace members and a Folder / File pair) and only this file changes; the screens do not.
import type { FileRow, FolderRow, UserForm, UserRow } from 'src/utils/lead-desk';

const call = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, { ...init, headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) } });
  const body = (await response.json().catch(() => ({}))) as T & { error?: string };

  if (!response.ok) throw new Error(body.error ?? `The server answered ${response.status}.`);

  return body;
};

// ------------------------------------------------------------------- users

export const fetchUsers = (): Promise<UserRow[]> => call<UserRow[]>('/api/users');

export const createUser = (form: UserForm): Promise<UserRow> =>
  call<UserRow>('/api/users', {
    method: 'POST',
    body: JSON.stringify({ firstName: form.firstName, lastName: form.lastName, email: form.email, role: form.role, password: form.password, isActive: form.isActive, reportsTo: form.role === 'TELECALLER' ? form.reportsTo : null }),
  });

export const updateUser = (id: string, patch: Partial<Pick<UserRow, 'firstName' | 'lastName' | 'email' | 'role' | 'isActive' | 'reportsTo'>> & { password?: string; currentPassword?: string }): Promise<UserRow> =>
  call<UserRow>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteUser = async (id: string): Promise<void> => {
  await call(`/api/users/${id}`, { method: 'DELETE' });
};

/** The people the signed-in person works with. Admin: everyone active. Counsellor: themself and their own telecallers. Telecaller: themself and their counsellor. */
export const fetchTeam = (): Promise<UserRow[]> => call<UserRow[]>('/api/team');

// ----------------------------------------------------------------- folders

export const fetchFolders = (): Promise<FolderRow[]> => call<FolderRow[]>('/api/folders');

export const createFolder = (name: string): Promise<FolderRow> => call<FolderRow>('/api/folders', { method: 'POST', body: JSON.stringify({ name }) });

export const deleteFolder = async (id: string): Promise<void> => {
  await call(`/api/folders/${id}`, { method: 'DELETE' });
};

export const fetchFiles = (folderId: string): Promise<FileRow[]> => call<FileRow[]>(`/api/folders/${folderId}/files`);

const toBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';

  // chunked, because String.fromCharCode(...bigArray) overflows the stack on a multi-megabyte file
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));

  return btoa(binary);
};

export const uploadFile = async (folderId: string, file: File): Promise<FileRow> =>
  call<FileRow>(`/api/folders/${folderId}/files`, { method: 'POST', body: JSON.stringify({ name: file.name, type: file.type, contentBase64: await toBase64(file) }) });

export const deleteFile = async (id: string): Promise<void> => {
  await call(`/api/files/${id}`, { method: 'DELETE' });
};

export const downloadHref = (id: string): string => `/api/files/${id}/download`;
